import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { useImmer } from 'use-immer'

import { useColumnToggle } from '../../../../../hooks/useColumnToggle'
import { ConfigurableTable } from '../../../../../utils/wrapper'
import { mutatePermissions } from '../../../../authorization/data/mutatePermissions'
import { useConfigContext } from '../../../../ui-config'
import { BackgroundTaskStatusContainer } from '../../../backgroundTaskStatus'
import { RoleAssignment, useAllRoleAssignments } from '../../data'
import { AddAssigneeContainer } from '../dialogs/AddAssignee'
import { DeleteAssigneeIdWrapper } from '../dialogs/DeleteAssignee/DeleteAssigneeIdWrapper'
import { DeleteAssigneesContainer } from '../dialogs/DeleteAssignee/DeleteAssignees.container'
import { EditAssigneeIdWrapper } from '../dialogs/EditAssignee/EditAssigneIdWrapper'

const STORAGE_KEY = 'role-assignments-visible-columns'
const TOGGLEABLE_FIELDS = ['projectName', 'status', 'role']

type RoleAssignmentsTableProps = {
  globalFilterValue: string
  setGlobalFilterValue: (value: string) => void
}

type RoleAssignmentsTableState = {
  createDialog: {
    isVisible: boolean
    isRequestRunning: boolean
  }
  editDialog: {
    isVisible: boolean
    isRequestRunning: boolean
    id?: string
  }
  deleteDialog: {
    isVisible: boolean
    isRequestRunning: boolean
    id?: string
  }
  deleteAssigneesDialog: {
    isVisible: boolean
    isRequestRunning: boolean
  }
  statusDialog: {
    isVisible: boolean
    isRequestRunning: boolean
    location?: string
    retryAfter?: string
    isCreate: boolean
    isDelete: boolean
  }
}

export const RoleAssignmentsTable: React.FC<RoleAssignmentsTableProps> = ({
  globalFilterValue,
  setGlobalFilterValue,
}) => {
  const { t } = useTranslation()
  const { uiConfig: config } = useConfigContext()
  const { effectiveTableConfig, navigationColumnHeader } = useColumnToggle(
    config.selfservices.roleAssignments.table,
    TOGGLEABLE_FIELDS,
    STORAGE_KEY,
    true,
  )
  const {
    data: roleAssignments,
    isLoading: isRoleAssignmentsLoading,
    mutate,
  } = useAllRoleAssignments()

  const [state, updateState] = useImmer<RoleAssignmentsTableState>({
    createDialog: { isRequestRunning: false, isVisible: false },
    editDialog: { isRequestRunning: false, isVisible: false, id: undefined },
    deleteDialog: { isRequestRunning: false, isVisible: false, id: undefined },
    deleteAssigneesDialog: {
      isRequestRunning: false,
      isVisible: false,
    },
    statusDialog: {
      isRequestRunning: false,
      isVisible: false,
      isCreate: false,
      isDelete: false,
      location: undefined,
      retryAfter: undefined,
    },
  })

  const [selectedProducts, setSelectedProducts] = useState<RoleAssignment[]>([])

  const triggerStatusRequest = (
    isCreateValue: boolean,
    isDeleteValue: boolean,
    location: string,
    retryAfter: string,
  ) => {
    updateState(draft => {
      draft.statusDialog.isVisible = true
      draft.statusDialog.isRequestRunning = true
      draft.statusDialog.isCreate = isCreateValue
      draft.statusDialog.isDelete = isDeleteValue
      draft.statusDialog.location = location
      draft.statusDialog.retryAfter = retryAfter

      return draft
    })
  }

  const onHideStatus = () => {
    updateState(draft => {
      draft.statusDialog.isVisible = false
      draft.statusDialog.isRequestRunning = false
      draft.statusDialog.location = undefined
      draft.statusDialog.retryAfter = undefined

      return draft
    })
  }

  const setIsCreateDialogVisible = (value: boolean) => {
    updateState(draft => {
      draft.createDialog.isVisible = value
      draft.deleteDialog.isVisible = !value
      draft.editDialog.isVisible = !value

      return draft
    })
  }
  const setIsEditDialogVisible = (rowData: RoleAssignment, value: boolean) => {
    updateState(draft => {
      draft.editDialog.id = rowData.id
      draft.createDialog.isVisible = !value
      draft.deleteDialog.isVisible = !value
      draft.editDialog.isVisible = value

      return draft
    })
  }
  const setIsDeleteDialogVisible = (id: string, value: boolean) => {
    updateState(draft => {
      draft.deleteDialog.id = id
      draft.createDialog.isVisible = !value
      draft.deleteDialog.isVisible = value
      draft.editDialog.isVisible = !value

      return draft
    })
  }

  const setIsDeleteAssigneesDialogVisible = (value: boolean) => {
    updateState(draft => {
      draft.createDialog.isVisible = !value
      draft.deleteDialog.isVisible = !value
      draft.editDialog.isVisible = !value
      draft.deleteAssigneesDialog.isVisible = value

      return draft
    })
  }

  const setIsCreateRequestRunning = (value: boolean) => {
    updateState(draft => {
      draft.createDialog.isRequestRunning = value

      return draft
    })
  }
  const setIsEditRequestRunning = (value: boolean) => {
    updateState(draft => {
      draft.editDialog.isRequestRunning = value

      return draft
    })
  }
  const setIsDeleteRequestRunning = (value: boolean) => {
    updateState(draft => {
      draft.deleteDialog.isRequestRunning = value

      return draft
    })
  }

  const setIsDeleteAssigneesRequestRunning = (value: boolean) => {
    updateState(draft => {
      draft.deleteAssigneesDialog.isRequestRunning = value

      return draft
    })
  }

  const onHideEditDialog = () => {
    updateState(draft => {
      draft.editDialog.isVisible = false
      draft.editDialog.id = undefined

      return draft
    })
  }

  const onHideDeleteDialog = () => {
    updateState(draft => {
      draft.deleteDialog.isVisible = false
      draft.deleteDialog.id = undefined

      return draft
    })
  }

  const onHideDeleteAssigneesDialog = () => {
    updateState(draft => {
      draft.deleteAssigneesDialog.isVisible = false
      setSelectedProducts([])

      return draft
    })
  }

  const onHideCreateDialog = () => {
    updateState(draft => {
      draft.createDialog.isVisible = false

      return draft
    })
  }

  const mutateHandler = () => {
    mutatePermissions()
    mutate()
  }

  const headerActions = (
    <>
      <Button
        label={t('roleAssignments.table.delete.label')}
        onClick={() => setIsDeleteAssigneesDialogVisible(true)}
        icon="pi pi-trash"
        className=""
        loading={state.deleteAssigneesDialog.isRequestRunning}
        disabled={selectedProducts.length > 0 ? false : true}
        outlined
      />
      <DeleteAssigneesContainer
        assignments={selectedProducts}
        setIsRequestRunning={setIsDeleteAssigneesRequestRunning}
        visible={state.deleteAssigneesDialog.isVisible}
        mutate={mutateHandler}
        onHide={onHideDeleteAssigneesDialog}
        triggerStatus={triggerStatusRequest}
      />
      <Button
        label={t('roleAssignments.table.add.label')}
        onClick={() => setIsCreateDialogVisible(true)}
        icon="pi pi-plus"
        className=""
        loading={state.createDialog.isRequestRunning}
      />
      <AddAssigneeContainer
        setIsRequestRunning={setIsCreateRequestRunning}
        visible={state.createDialog.isVisible}
        mutate={mutateHandler}
        onHide={onHideCreateDialog}
        triggerStatus={triggerStatusRequest}
      />
      <BackgroundTaskStatusContainer
        isCreate={state.statusDialog.isCreate}
        isDelete={state.statusDialog.isDelete}
        locationUrl={state.statusDialog.location!}
        visible={state.statusDialog.isVisible}
        retry={state.statusDialog.retryAfter}
        onHide={onHideStatus}
        mutate={mutateHandler}
      />
    </>
  )

  //  action template
  const actionBodyTemplate = (rowData: RoleAssignment) => {
    return (
      <>
        <div className="flex justify-content-end flex-nowrap">
          <div>
            <Button
              icon="pi pi-trash"
              className="p-button-rounded p-button-text mr-2"
              onClick={() => setIsDeleteDialogVisible(rowData.id, true)}
              severity="danger"
              loading={state.deleteDialog.isRequestRunning}
            />
          </div>
          <div>
            <Button
              icon="pi pi-pencil"
              className="p-button-rounded p-button-text"
              onClick={() => setIsEditDialogVisible(rowData, true)}
              loading={state.editDialog.isRequestRunning}
            />
          </div>
        </div>
      </>
    )
  }

  const buttons = (
    <Column
      key="buttons"
      body={actionBodyTemplate}
      header={
        <div className="flex justify-content-end">{navigationColumnHeader}</div>
      }
    />
  )

  return (
    <>
      <ConfigurableTable
        config={effectiveTableConfig}
        buttons={buttons}
        data={roleAssignments}
        dataKey="id"
        headerActions={headerActions}
        globalFilterFields={['assignee', 'project', 'projectName']}
        globalFilterValue={globalFilterValue}
        setGlobalFilterValue={setGlobalFilterValue}
        isLoading={isRoleAssignmentsLoading}
        stateKey="table-role-assignments"
        checkboxen={true}
        selected={selectedProducts}
        setSelected={setSelectedProducts}
      />

      <EditAssigneeIdWrapper
        setIsRequestRunning={setIsEditRequestRunning}
        id={state.editDialog.id}
        visible={state.editDialog.isVisible}
        mutate={mutateHandler}
        onHide={onHideEditDialog}
      />
      <DeleteAssigneeIdWrapper
        setIsRequestRunning={setIsDeleteRequestRunning}
        id={state.deleteDialog.id}
        visible={state.deleteDialog.isVisible}
        mutate={mutateHandler}
        onHide={onHideDeleteDialog}
      />
    </>
  )
}
